<template>
  <div class="row">
    <div class="col-sm-2">
      <OverviewBox title="My Profile" :loading="loading" :avatarImage="logoURL" :avatarName="userData.name" :avatarCircleMode="avatarCircleMode" :avatarEditable="true" @editImage="editAvatarImage" @removeImage="removeAvatarImage">
        <h4>{{userData.name}}</h4>
        <p>
          <span class="label label-account-owner" v-if="userData.isOwner">Account Owner</span>
        </p>
        <h5>User Groups</h5>
        <p>
          <span class="label label-empty label-overview-box mr-1" v-for="group in userData.userGroups" :key="group.id">
            <i class="fas fa-circle pr-1" :style="{'color': group.colourTag}"> </i> {{group.name}}
          </span>
        </p>
        <template v-if="userData.labelList && userData.labelList.length > 0">
          <h5>Labels</h5>
          <p>
            <span class="label label-empty label-overview-box mr-1" v-for="label in userData.labelList" :key="label.id"><i class="fas fa-circle pr-1" :style="{color: label.colour}"> </i> {{label.name}}</span>
          </p>
        </template>
        <OverviewBoxInfoItem icon="fa-user">{{userData.title}} {{userData.name}}</OverviewBoxInfoItem>
        <OverviewBoxInfoItem v-if="userData.phone" icon="fa-phone">{{userData.phone}}</OverviewBoxInfoItem>
        <OverviewBoxInfoItem v-if="userData.mobile" icon="fa-mobile-alt">{{userData.mobile}}</OverviewBoxInfoItem>

        <OverviewBoxList>
          <OverviewBoxListItem title="Status">
            <span v-if="userData.isActive" class="status-active">Active</span>
            <span v-if="!userData.isActive" class="status-archive">Archived</span>
          </OverviewBoxListItem>
          <OverviewBoxListItem title="Created On">{{userData.createdAt | formatDate}}</OverviewBoxListItem>
          <OverviewBoxListItem title="Last Updated">{{userData.updatedAt | formatDate}}</OverviewBoxListItem>
          <OverviewBoxListItem title="Last Login">{{userData.lastLogin | formatDateTime}}</OverviewBoxListItem>
        </OverviewBoxList>
      </OverviewBox>
    </div>
    <div class="col-sm-10">
      <TabbedBox :loading="loading">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-1">
            <i class="far fa-user"></i> My Profile
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-1">
            <DataPageArea title="Details">
              <template slot="actionArea">
                <ActionDropMenu class="float-right">
                  <DropdownMenuItem icon="fa-key" @click="profileChangePassword">Change Password</DropdownMenuItem>
                  <DropdownMenuDivider />
                  <DropdownMenuItem icon="fa-mobile" @click="displayMFASettings">Multi-Factor Settings</DropdownMenuItem>
                </ActionDropMenu>
              </template>
              <template slot="dataArea">
                <DataItem title="Name">{{userData.title}} {{userData.name}}</DataItem>
                <DataItem title="Email">{{userData.email}}</DataItem>
                <DataItem title="Status">
                  <span v-if="userData.isActive" class="status-active">Active</span>
                  <span v-if="!userData.isActive" class="status-archive">Archived</span>
                </DataItem>
                <DataItem title="Phone">{{userData.phone}}</DataItem>
                <DataItem title="Mobile">{{userData.mobile}}</DataItem>
                <DataItem title="Timezone">{{userData.timezoneCode}}</DataItem>
                <DataItem title="DOB">{{userData.dateOfBirth | formatDate}}</DataItem>
              </template>
            </DataPageArea>
          </TabPage>
        </template>
      </TabbedBox>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import form from '@/mixins/form'

import UserChangePasswordDialog from '@/modules/users/views/userChangePasswordDialog.vue'
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'
import UserMFASettingsDialog from '@/modules/users/views/userMFASettingsDialog'
import colourMixin from '@/mixins/colour'

export default {
  name: "User",
  mixins: [form, colourMixin],
  components: {
  },
  data: () => {
    return {
      breadcrumbs: [
        { title: "Users", link: "/users" },
        { title: "Users" }
      ],
      loading: false,
      userData: {}
    };
  },
  watch: {
    userId: {
      immediate: true,
      handler: function() {
        this.requestData()
      }
    }
  },
  mounted() {
    this.loading = true
    this.requestData()
  },
  computed: {
    ...mapState({
      profileUser: state => state.profile.profileUser,
      userId: state => state.profile.profileUser.id,
    }),    
    logoURL() {
      if(this.$data.userData.logoURL == null || this.$data.userData.logoURL == '') {
        if(this.$data.userData.title == 'Ms' || this.$data.userData.title == 'Miss' || this.$data.userData.title == 'Mrs')
          return '/glass/img/user-overview-female.png'
        else
          return '/glass/img/user-overview-male.png'
      }
      return this.$data.userData.logoURL
    },
    avatarCircleMode() {
      return this.$data.userData.logoURL != null && this.$data.userData.logoURL != ''
    }
  },
  methods: {
    requestData() {
      if(!this.userId)
        return

      this.loading = true

      this.$apiService.userService.getUser(this.userId)
      .then(response => {
        this.$data.userData = response.data;

        this.$store.commit("page/setPageTitle", this.$data.userData.name);
      })
      .finally(() => {
        this.loading = false
      })
    },
    profileChangePassword() {
      this.displayModal(UserChangePasswordDialog)
      .then((success) => {
        if(success) {
          this.successToast('Password Updated')
          this.requestData()
        }
      })
    },
    displayMFASettings() {
      this.displayModal(UserMFASettingsDialog, { userId: this.userId }, this.userData)
      .then((success) => {
        if(success) {
          this.successToast('MFA Updated')
          this.requestData()
        }
      })
    },
    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.userData.id,
        APIresource: 'users',
        currentURL: this.$data.userData.logoURL,
        circleCrop: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestData()
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this user\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.userData.id, 'users').then(response => {
            this.successToast('Image Removed')
            this.requestData()
          })
        }
      })
    }
  }
}

</script>